import React, { useEffect, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Divider,
} from "@mui/material";
import { createAdmin, getAllReportViewers } from "../../../services/adminServices/AdminServices";
import { assignAdminToCoe } from "../../../services/adminServices/CoeServices";
import { toast } from "react-toastify";

const INITIAL_FORM_STATE = {
    email: "",
    mobile: "",
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: 460 },
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
    outline: "none !important",
};

const AddNewViewerModal = ({
    open,
    onClose,
    coeId,
    coeName,
    fetchData,
    fetchCoeData,
    onSwitchToAssignExisting,
}) => {
    const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setFormValues(INITIAL_FORM_STATE);
            setFormErrors({});
            setIsLoading(false);
        }
    }, [open]);

    const handleInputChange = ({ target: { name, value } }) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateFields = () => {
        const errors = {};
        const { email, mobile } = formValues;

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email?.trim()) {
            errors.email = "Email is required*";
        } else if (!emailRegex.test(email.trim())) {
            errors.email = "Enter a valid email address*";
        }

        const mobileRegex = /^[6-9][0-9]{9}$/;
        if (!mobile?.trim()) {
            errors.mobile = "Mobile is required*";
        } else if (!mobileRegex.test(mobile.trim())) {
            errors.mobile = "Enter a valid 10-digit mobile number*";
        }

        return errors;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateFields();
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        const { email, mobile } = formValues;
        const payload = {
            email: email.trim(),
            mobile_number: mobile.trim(),
            role: "coe_report_viewer",
        };

        setIsLoading(true);
        try {
            // 1. Create the admin as report viewer, passing coeId
            const response = await createAdmin(payload, coeId);

            // 2. Ensure viewer is linked to the COE center
            let adminId = response?.data?.data?.ejet_admin_id || response?.data?.data?.id;

            if (!adminId) {
                try {
                    const viewersRes = await getAllReportViewers();
                    const found = (viewersRes?.data?.data || []).find(
                        (v) => v.email?.toLowerCase() === payload.email.toLowerCase()
                    );
                    if (found) {
                        adminId = found.ejet_admin_id || found.id;
                    }
                } catch (lookupErr) {
                    console.error("Error finding created viewer ID:", lookupErr);
                }
            }

            if (adminId && coeId) {
                try {
                    await assignAdminToCoe({
                        ejet_coe_center_id: coeId,
                        ejet_admin_id: adminId,
                    });
                } catch (assignErr) {
                    // Ignored if already mapped by createAdmin query param
                    console.log("Assignment info:", assignErr);
                }
            }

            toast.success(response?.data?.message || "Report viewer created and added successfully");
            if (typeof fetchData === "function") fetchData();
            if (typeof fetchCoeData === "function") fetchCoeData();
            onClose();
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Something went wrong while creating viewer");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center">
                    Add New Report Viewer
                </Typography>
                <Divider sx={{ my: 2 }} />
                <form onSubmit={handleSubmit}>
                    <FormControl fullWidth size="small" margin="dense" sx={{ mb: 2 }}>
                        <InputLabel id="viewer-role-label">Select Role *</InputLabel>
                        <Select
                            labelId="viewer-role-label"
                            value="coe_report_viewer"
                            label="Select Role *"
                            disabled
                        >
                            <MenuItem value="coe_report_viewer">Report Viewer</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        size="small"
                        label="Email *"
                        name="email"
                        type="email"
                        placeholder="viewer@example.com"
                        value={formValues.email}
                        onChange={handleInputChange}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        margin="dense"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Mobile *"
                        name="mobile"
                        placeholder="10-digit mobile number"
                        value={formValues.mobile}
                        onChange={handleInputChange}
                        error={!!formErrors.mobile}
                        helperText={formErrors.mobile}
                        margin="dense"
                        inputProps={{ maxLength: 10 }}
                        sx={{ mb: 2 }}
                    />

                    {onSwitchToAssignExisting && (
                        <Box sx={{ mb: 2, textAlign: "left" }}>
                            <Typography variant="caption" color="text.secondary">
                                Already created in Admin?{" "}
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => {
                                        onClose();
                                        onSwitchToAssignExisting();
                                    }}
                                    sx={{
                                        textTransform: "none",
                                        p: 0,
                                        minWidth: 0,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        verticalAlign: "baseline",
                                    }}
                                >
                                    Assign Existing Viewer
                                </Button>
                            </Typography>
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="flex-end" gap={1.5}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={onClose}
                            disabled={isLoading}
                            sx={{ textTransform: "capitalize", borderRadius: "8px" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isLoading || !formValues.email?.trim() || !formValues.mobile?.trim()}
                            sx={{ textTransform: "capitalize", borderRadius: "8px", minWidth: 100 }}
                        >
                            {isLoading ? <CircularProgress size={22} color="inherit" /> : "Create"}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default AddNewViewerModal;
